import { useRef, useEffect, useCallback } from 'react';

import {
  useBraintreeExecuteMutation,
  BraintreeExecuteRequest,
  BraintreeExecuteResponse,
} from '@/api/payments/braintreeExecute';
import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';
import { UserDetailsState } from '@/features/userDetails/userDetailsSlice';
import { TransactionState } from '@/features/paymentProcess/transactionSlice';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';

const buildExecutePayload = (
  paymentSelectState: PaymentSelectState,
  userDetailsState: UserDetailsState,
  transactionState: TransactionState,
  client: string,
  accountIdentifier: string,
  nonce: string,
): BraintreeExecuteRequest => ({
  client,
  nonce,
  transactionId: transactionState.transactionId,
  account_identifier: accountIdentifier,
  amount: paymentSelectState.amount,
  currency: paymentSelectState.currency,
  firstName: userDetailsState.firstName,
  lastName: userDetailsState.lastName,
  email: userDetailsState.email,
});

export function useBraintreeExecute() {
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const userDetailsState = useAppSelector(state => state.userDetails);
  const transactionState = useAppSelector(state => state.transaction);

  const { getProviderBraintreeAccountIdentifier } = usePaymentsConfig();

  const braintreeAccountIdentifier = getProviderBraintreeAccountIdentifier();

  const [braintreeExecuteTrigger, {
    isLoading,
    data,
    error,
  }] = useBraintreeExecuteMutation();

  const { getClientId } = useClient();

  const onSuccess = useRef<((data: BraintreeExecuteResponse) => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);

  // Trigger payment-service execute
  const triggerExecuteApi = useCallback(async (
    nonce: string,
    callbacks?: {
      onSuccess?: (data: BraintreeExecuteResponse) => void,
      onError?: (error?: unknown) => void,
    }
  ) => {
    onSuccess.current = callbacks?.onSuccess;
    onError.current = callbacks?.onError;

    if (!nonce) {
      logger.error('Braintree nonce not set');
      if (onError.current) {
        onError.current(new Error('Braintree nonce not set'));
      }
      return;
    }

    // Require braintree provider config
    if (!braintreeAccountIdentifier) {
      logger.error('Error accessing Braintree config');
      if (onError.current) {
        onError.current(new Error('Error accessing Braintree config'));
      }
      throw new Error('Error accessing Braintree config');
    }

    try {
      const payload = buildExecutePayload(
        paymentSelectState,
        userDetailsState,
        transactionState,
        getClientId(),
        braintreeAccountIdentifier,
        nonce,
      );
      await braintreeExecuteTrigger(payload);
    } catch (err) {
      logger.error('Error executing Braintree transaction');
      logger.error(err);
      if (onError.current) {
        onError.current(err);
      }
    }
  }, [braintreeAccountIdentifier, paymentSelectState, userDetailsState, transactionState, getClientId, braintreeExecuteTrigger]);

  // Handle payment-service execute complete
  useEffect(() => {
    // Wait until payment-service execute is complete
    if (isLoading || (!data && !error)) {
      return;
    }

    // Handle payment-service execute errors
    if (!!error || data?.message !== 'success') {
      logger.error('Error executing Braintree transaction');
      logger.error(error);
      if (onError.current) {
        onError.current(error);
      }
      return;
    }

    if (onSuccess.current) {
      onSuccess.current(data);
    }
  }, [isLoading, data, error, onSuccess, onError]);

  return {
    triggerExecuteApi,
    executeIsLoading: isLoading,
    executeData: data,
    executeError: error,
  };
}
